import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import api from "../services/api";
import "./EmailComposer.css";

interface EmailComposerProps {
  candidateId?: string | number;
  jobId?: string | number;
  customerId?: string | number;
  mode?: "recruiter" | "sales";
  onClose?: () => void;
  onSent?: (result: EmailSendResult) => void;
}

interface Template {
  id: number;
  name: string;
  is_organization_template?: boolean;
}

interface TemplatesResponse {
  data?: Template[];
}

interface Candidate {
  first_name: string;
  last_name: string;
  email: string;
}

interface Job {
  title: string;
}

interface Customer {
  name?: string;
  company_name?: string;
}

interface EntityResponse<T> {
  data?: T;
}

interface EmailGenerateResult {
  success?: boolean;
  data?: {
    subject?: string;
    body?: string;
  };
}

interface EmailSendResult {
  success?: boolean;
  [key: string]: unknown;
}

type EmailStyle =
  | "professional and engaging"
  | "friendly and casual"
  | "formal and structured"
  | "persuasive and compelling";

const EmailComposer: React.FC<EmailComposerProps> = ({
  candidateId,
  jobId,
  customerId,
  mode = "recruiter",
  onClose,
  onSent,
}) => {
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [useAI, setUseAI] = useState(true);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation(["recruiting", "common"]);
  const [emailPromptApproach, setEmailPromptApproach] = useState<EmailStyle>(
    "professional and engaging",
  );

  // Fetch templates (user templates + organization templates)
  const { data: templatesData } = useQuery<TemplatesResponse>(
    ["templates", mode],
    () =>
      api.communication.getTemplates({
        category: mode,
        type: "outreach",
      } as Record<string, unknown>) as Promise<TemplatesResponse>,
    { enabled: true },
  );

  const allTemplates = templatesData?.data || [];

  // Separate organization templates from user templates
  const organizationTemplates = allTemplates.filter(
    (t) => t.is_organization_template,
  );
  const userTemplates = allTemplates.filter((t) => !t.is_organization_template);

  // Fetch candidate/job/customer data for context
  const { data: candidateData } = useQuery<EntityResponse<Candidate>>(
    ["candidate", candidateId],
    () =>
      api.recruiting.getCandidate(candidateId) as Promise<
        EntityResponse<Candidate>
      >,
    { enabled: !!candidateId },
  );

  const { data: jobData } = useQuery<EntityResponse<Job>>(
    ["job", jobId],
    () => api.recruiting.getJob(jobId) as Promise<EntityResponse<Job>>,
    { enabled: !!jobId },
  );

  const { data: customerData } = useQuery<EntityResponse<Customer>>(
    ["customer", customerId],
    () =>
      api.recruiting.getCustomer(customerId) as Promise<
        EntityResponse<Customer>
      >,
    { enabled: !!customerId },
  );

  // Generate email
  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      let result: EmailGenerateResult;

      if (mode === "recruiter") {
        result = (await api.communication.generateRecruiterEmail({
          candidate_id: candidateId,
          job_id: jobId,
          template_id: templateId,
          useAI,
          emailPromptApproach,
        })) as EmailGenerateResult;
      } else {
        result = (await api.communication.generateSalesEmail({
          candidate_id: candidateId,
          customer_id: customerId,
          job_id: jobId,
          template_id: templateId,
          useAI,
          emailPromptApproach,
        })) as EmailGenerateResult;
      }

      if (result.success && result.data) {
        setSubject(result.data.subject || "");
        setBody(result.data.body || "");
      }
    } catch (err) {
      const error = err as Error & {
        response?: { data?: { error?: string; message?: string } };
      };
      setError(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          t("email.failedToGenerate"),
      );
    } finally {
      setIsGenerating(false);
    }
  };

  // Send email
  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      setError(t("email.subjectAndBodyRequired"));
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      let result: EmailSendResult;

      if (mode === "recruiter") {
        result = (await api.communication.sendRecruiterEmail({
          candidate_id: candidateId,
          job_id: jobId,
          template_id: templateId,
          useAI,
          emailPromptApproach,
        })) as EmailSendResult;
      } else {
        result = (await api.communication.sendSalesEmail({
          candidate_id: candidateId,
          customer_id: customerId,
          job_id: jobId,
          template_id: templateId,
          useAI,
          emailPromptApproach,
        })) as EmailSendResult;
      }

      if (result.success) {
        if (onSent) onSent(result);
        if (onClose) onClose();
      }
    } catch (err) {
      const error = err as Error & {
        response?: { data?: { error?: string; message?: string } };
      };
      setError(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          t("email.failedToSend"),
      );
    } finally {
      setIsSending(false);
    }
  };

  // Auto-generate on mount if candidate/job/customer data is available
  // Track if we've already auto-generated
  const hasAutoGenerated = useRef(false);

  useEffect(() => {
    if (candidateData && (jobId || customerId) && !hasAutoGenerated.current) {
      hasAutoGenerated.current = true;
      handleGenerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateData, jobId, customerId]);

  const candidate = candidateData?.data;
  const job = jobData?.data;
  const customer = customerData?.data;

  return (
    <div className="email-composer">
      <div className="composer-header">
        <h2>
          {mode === "recruiter"
            ? t("email.emailCandidate")
            : t("email.emailCustomer")}
        </h2>
        <button className="close-btn" onClick={onClose}>
          ×
        </button>
      </div>

      <div className="composer-content">
        {error && <div className="error-message">{error}</div>}

        {/* Context Info */}
        <div className="context-info">
          {candidate && (
            <div className="context-item">
              <strong>{t("email.candidateLabel")}</strong>{" "}
              {candidate.first_name} {candidate.last_name} ({candidate.email})
            </div>
          )}
          {job && (
            <div className="context-item">
              <strong>{t("email.jobLabel")}</strong> {job.title}
            </div>
          )}
          {customer && (
            <div className="context-item">
              <strong>{t("email.customerLabel")}</strong>{" "}
              {customer.name || customer.company_name}
            </div>
          )}
        </div>

        {/* Template Selection */}
        <div className="form-group">
          <label htmlFor="template-select">{t("email.templateOptional")}</label>
          <select
            id="template-select"
            value={templateId || ""}
            onChange={(e) =>
              setTemplateId(
                e.target.value ? Number.parseInt(e.target.value, 10) : null,
              )
            }
          >
            <option value="">{t("email.noTemplate")}</option>

            {/* Organization Templates */}
            {organizationTemplates.length > 0 && (
              <optgroup label={t("email.organizationTemplates")}>
                {organizationTemplates.map((tmpl) => (
                  <option key={tmpl.id} value={tmpl.id}>
                    {tmpl.name}
                  </option>
                ))}
              </optgroup>
            )}

            {/* User Templates */}
            {userTemplates.length > 0 && (
              <optgroup label={t("email.myTemplates")}>
                {userTemplates.map((tmpl) => (
                  <option key={tmpl.id} value={tmpl.id}>
                    {tmpl.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>

        {/* AI Options */}
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={useAI}
              onChange={(e) => setUseAI(e.target.checked)}
            />{" "}
            {t("email.useAiGeneration")}
          </label>
        </div>

        {useAI && (
          <div className="form-group">
            <label htmlFor="email-style-select">{t("email.emailStyle")}</label>
            <select
              id="email-style-select"
              value={emailPromptApproach}
              onChange={(e) =>
                setEmailPromptApproach(e.target.value as EmailStyle)
              }
            >
              <option value="professional and engaging">
                {t("email.styles.professionalAndEngaging")}
              </option>
              <option value="friendly and casual">
                {t("email.styles.friendlyAndCasual")}
              </option>
              <option value="formal and structured">
                {t("email.styles.formalAndStructured")}
              </option>
              <option value="persuasive and compelling">
                {t("email.styles.persuasiveAndCompelling")}
              </option>
            </select>
          </div>
        )}

        {/* Subject */}
        <div className="form-group">
          <label htmlFor="email-subject">{t("email.subject")}</label>
          <input
            id="email-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t("email.subjectPlaceholder")}
          />
        </div>

        {/* Body */}
        <div className="form-group">
          <label htmlFor="email-body">{t("email.body")}</label>
          <textarea
            id="email-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t("email.bodyPlaceholder")}
            rows={15}
          />
        </div>

        {/* Actions */}
        <div className="composer-actions">
          <button
            className="btn-secondary"
            onClick={handleGenerate}
            disabled={isGenerating || isSending}
          >
            {isGenerating ? t("email.generating") : t("email.regenerate")}
          </button>
          <button
            className="btn-primary"
            onClick={handleSend}
            disabled={
              !subject.trim() || !body.trim() || isSending || isGenerating
            }
          >
            {isSending ? t("email.sending") : t("email.sendEmail")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailComposer;
